// ═══════════════════════════════════════════════════════════════════════
// 9. MEMORIAL — Tinch (m01) — quiet, single-page, no interactive features
// ═══════════════════════════════════════════════════════════════════════
//
// Design discipline for Tinch is silence. The job of this template is to
// disappear and let grief breathe. Every flourish you find yourself wanting
// to add — remove it.
//
// What's intentionally NOT here:
//   • No music, no countdown, no RSVP, no celebratory chimes, no fanfare.
//   • No sound triggers. Not even on the share button. (See note below.)
//   • No translate/scale/rotate animation. Movement reads as disrespect.
//   • No bright colors. Stone, charcoal, warm dark — and a hairline of bg.
//
// What IS here, and why:
//   • A museum-mounted portrait: outer hairline, mat, inner hairline,
//     faint warm sepia. (No drop-shadow — that reads as decoration.)
//   • The Quranic mourning recitation as a three-line typographic moment:
//     Arabic script, Uzbek transliteration, Uzbek meaning. All Marcellus,
//     generous leading.
//   • Years "1948 — 2026" as a pure typography moment: italic, true em-dash,
//     wide tracking, a hairline rule beneath. No box, no ornament.
//   • A near-invisible paper-fibre noise overlay (feTurbulence) for depth
//     without decoration. ~6% opacity.
//   • Slow opacity-only reveals (1.8s) via IntersectionObserver, fully
//     disabled under prefers-reduced-motion.
//   • A "Du'a / yordam" section — the Uzbek/Islamic equivalent of "in lieu
//     of flowers": invite the visitor to recite a prayer for the deceased.
//   • An inline, single-line "Xotira daftari" — local-only, plain text,
//     no submit fanfare, no list display. A guest leaves one quiet line
//     and the form thanks them. That's it.
//   • One signature element: a single candle in the hero corner with a
//     very slow flame opacity pulse (1.5s, 0.4↔0.6, opacity-only — no
//     scale, no rotate). prefers-reduced-motion → static flame.
//
// Sound: the memorial is meant to be silent. We deliberately do not import
// useSound or register any sound hook, to avoid even creating an
// AudioContext on this page. Zero sound triggers.

import { useEffect, useRef, useState } from "react";
import type { CSSProperties, ReactNode } from "react";
import { MapSection, ShareSection, GuestFooter } from "@/components/sections";
import { useT } from "@/lib/i18n";
import type { VariantProps } from "./types";

// ─── prefers-reduced-motion ────────────────────────────────────────────

function usePrefersReducedMotion(): boolean {
  const [reduce, setReduce] = useState<boolean>(() => {
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") return false;
    return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  });
  useEffect(() => {
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") return;
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    const onChange = (e: MediaQueryListEvent) => setReduce(e.matches);
    mq.addEventListener?.("change", onChange);
    return () => mq.removeEventListener?.("change", onChange);
  }, []);
  return reduce;
}

// ─── slow, dignified, opacity-only reveal ─────────────────────────────

interface QuietRevealProps {
  children: ReactNode;
  delay?: number;
  style?: CSSProperties;
  className?: string;
}

function QuietReveal({ children, delay = 0, style, className }: QuietRevealProps) {
  const reduce = usePrefersReducedMotion();
  const ref = useRef<HTMLDivElement | null>(null);
  const [shown, setShown] = useState<boolean>(reduce);

  useEffect(() => {
    if (reduce) {
      setShown(true);
      return;
    }
    const el = ref.current;
    if (!el || typeof IntersectionObserver === "undefined") {
      setShown(true);
      return;
    }
    const obs = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            setShown(true);
            obs.disconnect();
            break;
          }
        }
      },
      { threshold: 0.08, rootMargin: "0px 0px -40px 0px" },
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [reduce]);

  return (
    <div
      ref={ref}
      className={className}
      style={{
        ...style,
        opacity: shown ? 1 : 0,
        // No translate, no scale. Pure opacity. Slow. Dignified.
        transition: reduce ? "none" : `opacity 1.8s ease ${delay}ms`,
        willChange: reduce ? undefined : "opacity",
      }}
    >
      {children}
    </div>
  );
}

// ─── candle (signature element) ─────────────────────────────────────────

function Candle({
  ink,
  accent,
  bg,
  reduce,
  size = 56,
}: {
  ink: string;
  accent: string;
  bg: string;
  reduce: boolean;
  size?: number;
}) {
  return (
    <svg
      aria-hidden
      viewBox="0 0 24 32"
      width={size}
      height={size * (32 / 24)}
      style={{ display: "block" }}
    >
      <path
        d="M12 2 C 13.6 5 15 7 15 9.5 C 15 11.6 13.6 13 12 13 C 10.4 13 9 11.6 9 9.5 C 9 7 10.4 5 12 2 Z"
        fill={accent}
        style={{
          opacity: reduce ? 0.6 : undefined,
          animation: reduce ? "none" : "tinchFlame 1.5s ease-in-out infinite",
        }}
      />
      <path
        d="M12 5 C 12.8 7 13.4 8.2 13.4 9.6 C 13.4 10.7 12.8 11.4 12 11.4 C 11.2 11.4 10.6 10.7 10.6 9.6 C 10.6 8.2 11.2 7 12 5 Z"
        fill={bg}
        opacity="0.55"
      />
      <line x1="12" y1="13" x2="12" y2="15" stroke={ink} strokeWidth="0.6" />
      <rect x="9.5" y="15" width="5" height="14" fill={ink} opacity="0.85" />
      <rect x="9.5" y="15" width="5" height="1.2" fill={ink} opacity="0.45" />
      <rect x="8" y="29" width="8" height="1.6" fill={ink} opacity="0.7" />
    </svg>
  );
}

// ─── faint paper-fibre noise (feTurbulence), pinned full-bleed ─────────

function PaperNoise({ ink }: { ink: string }) {
  // Detect mobile viewport once for SVG-attribute swap. baseFrequency is an
  // SVG attribute (not stylable via CSS), so we render a higher frequency
  // value on small screens — smaller pattern tile, no pixelation.
  const [isMobile, setIsMobile] = useState<boolean>(false);
  useEffect(() => {
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") return;
    const mq = window.matchMedia("(max-width: 768px)");
    const apply = () => setIsMobile(mq.matches);
    apply();
    mq.addEventListener?.("change", apply);
    return () => mq.removeEventListener?.("change", apply);
  }, []);

  return (
    <svg
      aria-hidden
      style={{
        position: "fixed",
        inset: 0,
        width: "100vw",
        height: "100vh",
        pointerEvents: "none",
        opacity: 0.05,
        zIndex: 0,
        mixBlendMode: "multiply",
      }}
    >
      <filter id="tinch-paper">
        <feTurbulence
          type="fractalNoise"
          baseFrequency={isMobile ? "1.4" : "0.85"}
          numOctaves="2"
          stitchTiles="stitch"
        />
        <feColorMatrix
          // Tint the noise toward warm-dark ink.
          type="matrix"
          values={`0 0 0 0 0.16  0 0 0 0 0.14  0 0 0 0 0.12  0 0 0 0.85 0`}
        />
      </filter>
      <rect width="100%" height="100%" filter="url(#tinch-paper)" />
      {/* ink referenced to satisfy lint — keep deps explicit. */}
      <desc>{ink}</desc>
    </svg>
  );
}

// ─── variant ───────────────────────────────────────────────────────────

export function VariantMemorial({ t, isDark }: VariantProps) {
  const reduce = usePrefersReducedMotion();
  const tt = useT();
  const photo = t.photos && t.photos.length ? t.photos[0] : undefined;

  // Inline, single-line Xotira daftari — local only, no submit fanfare.
  const [memoryName, setMemoryName] = useState("");
  const [memoryLine, setMemoryLine] = useState("");
  const [thanked, setThanked] = useState(false);

  const onLeaveMemory = () => {
    if (!memoryLine.trim() || !memoryName.trim()) return;
    setThanked(true);
    // We deliberately do not POST or persist — this is a quiet inline gesture.
  };

  return (
    <>
      {/* Local keyframes for the candle flame. Opacity-only. */}
      <style>{`
        @keyframes tinchFlame {
          0%, 100% { opacity: 0.4; }
          50%      { opacity: 0.65; }
        }
        @media (prefers-reduced-motion: reduce) {
          @keyframes tinchFlame { 0%, 100% { opacity: 0.6; } }
        }

        .tinch-variant a:focus-visible,
        .tinch-variant button:focus-visible {
          outline: 2px solid ${t.accent};
          outline-offset: 2px;
        }

        /* ── Mobile lockdown ───────────────────────────────────────────
           Restraint > addition. Smaller viewport = every flourish more
           visible. Silence the flame, tighten the typography, breathe. */
        @media (max-width: 768px) {
          .tinch-variant button, .tinch-variant a { min-height: 44px; }
          .tinch-variant .tinch-mourning-header {
            display: block;
          }
          .tinch-variant .tinch-hero {
            padding: 64px 18px 48px !important;
            min-height: auto !important;
          }
          .tinch-variant .tinch-eyebrow {
            font-size: 11px !important;
            font-style: italic;
            font-family: var(--font-marcellus, var(--font-display, serif)) !important;
            font-weight: 400 !important;
            letter-spacing: 0.4em !important;
            color: var(--tinch-charcoal, #3A352E) !important;
            margin-bottom: 40px !important;
          }
          .tinch-variant .tinch-frame {
            width: 75vw !important;
            margin: 40px auto !important;
            box-shadow:
              inset 0 0 0 1px var(--tinch-bg, #EFEAE2),
              inset 0 0 0 2px rgba(58,53,46,0.18),
              inset 0 0 12px rgba(42,37,32,0.08) !important;
          }
          .tinch-variant .tinch-name {
            font-size: 40px !important;
            font-style: italic;
            letter-spacing: 0.02em !important;
            line-height: 1.15 !important;
          }
          .tinch-variant .tinch-years-line {
            font-size: 24px !important;
            letter-spacing: 0.05em !important;
          }
          .tinch-variant .tinch-years-rule {
            width: 30% !important;
          }
          .tinch-variant .tinch-hero-copy {
            font-size: 16px !important;
            line-height: 1.75 !important;
          }
          .tinch-variant .tinch-marosim {
            padding: 18px 24px !important;
          }
          .tinch-variant .tinch-marosim-date {
            font-size: 18px !important;
          }

          /* Quranic verse — three-line typographic mihrab. */
          .tinch-variant .tinch-recitation {
            padding: 60px 24px !important;
          }
          .tinch-variant .tinch-arabic {
            font-size: 22px !important;
            line-height: 2.0 !important;
          }
          .tinch-variant .tinch-translit {
            font-size: 14px !important;
            line-height: 1.7 !important;
          }
          .tinch-variant .tinch-meaning {
            font-size: 14px !important;
          }

          /* Du'a section. */
          .tinch-variant .tinch-dua {
            padding: 48px 24px !important;
          }
          .tinch-variant .tinch-dua-inner {
            max-width: 86% !important;
          }
          .tinch-variant .tinch-dua-line {
            font-size: 16px !important;
            line-height: 1.7 !important;
          }
          .tinch-variant .tinch-dua-sub {
            font-size: 14px !important;
          }

          /* Xotira daftari — full-width input + small submit below. */
          .tinch-variant .tinch-ledger {
            padding: 56px 24px !important;
          }
          .tinch-variant .tinch-ledger-input {
            height: 54px !important;
            padding: 0 14px !important;
            font-size: 16px !important;
            border: 1px solid rgba(58,53,46,0.25) !important;
            border-bottom: 1px solid rgba(58,53,46,0.25) !important;
            background: rgba(255,255,255,0.4) !important;
          }
          .tinch-variant .tinch-ledger-submit {
            display: inline-block !important;
            margin-top: 16px !important;
            padding: 10px 20px !important;
            font-size: 11px !important;
            letter-spacing: 0.28em !important;
          }
          .tinch-variant .tinch-ledger-thanks {
            font-size: 16px !important;
          }

          /* Candle signature — static on mobile (lockdown).
             Slightly larger than desktop's hero candle, anchored to safe-area. */
          .tinch-variant .tinch-candle-wrap {
            right: max(20px, env(safe-area-inset-right, 20px)) !important;
            bottom: max(20px, env(safe-area-inset-bottom, 20px)) !important;
            opacity: 0.5 !important;
          }
          .tinch-variant .tinch-candle-wrap svg {
            width: 40px !important;
            height: auto !important;
          }
          .tinch-variant .tinch-candle-wrap svg path,
          .tinch-variant .tinch-candle-wrap svg path *,
          .tinch-variant .tinch-candle-wrap svg * {
            animation: none !important;
          }

          /* Map: 16:9 placeholder, address in italic Marcellus 14px. */
          .tinch-variant .tinch-map-wrap {
            padding: 0 24px !important;
          }
        }
      `}</style>

      <PaperNoise ink={t.ink} />

      <div className="tinch-variant" style={{ position: "relative", zIndex: 1 }}>
        {/* Mobile-only mourning recitation header — quiet, before the eyebrow. */}
        <div
          className="tinch-mourning-header"
          aria-hidden={false}
          style={{
            display: "none",
            padding: "20px 24px 0",
            textAlign: "center",
            opacity: 0.6,
          }}
        >
          <div
            lang="ar"
            dir="rtl"
            style={{
              fontFamily: t.font,
              fontSize: 14,
              lineHeight: 1.8,
              color: t.ink,
              letterSpacing: "0.02em",
            }}
          >
            إِنَّا لِلَّهِ وَإِنَّا إِلَيْهِ رَاجِعُونَ
          </div>
          <div
            style={{
              fontFamily: t.font,
              fontStyle: "italic",
              fontSize: 11,
              marginTop: 6,
              color: `${t.ink}cc`,
              letterSpacing: "0.04em",
            }}
          >
            {tt("variant.m01.mourning.translit")}
          </div>
        </div>
        {/* ── HERO ─────────────────────────────────────────────────── */}
        <section
          className="tinch-hero"
          style={{
            minHeight: "100vh",
            padding: "96px 32px",
            display: "grid",
            placeItems: "center",
            textAlign: "center",
            position: "relative",
          }}
        >
          <QuietReveal style={{ maxWidth: 600, width: "100%" }}>
            <div
              className="tinch-eyebrow"
              style={{
                fontFamily: "var(--font-ui)",
                fontSize: 11,
                fontWeight: 700,
                letterSpacing: "0.5em",
                color: `${t.ink}aa`,
                marginBottom: 56,
              }}
            >
              {tt("variant.m01.hero.eyebrow")}
            </div>

            {/* Museum-mounted portrait. */}
            <div
              className="tinch-frame"
              style={{
                width: "min(280px, 62vw)",
                aspectRatio: "3/4",
                margin: "0 auto 56px",
                border: `1px solid ${t.ink}55`,
                padding: 12,
                background: t.bg,
                boxShadow: `inset 0 0 0 1px ${t.bg}, inset 0 0 0 2px ${t.ink}1A`,
              }}
            >
              <div
                style={{
                  width: "100%",
                  height: "100%",
                  position: "relative",
                  outline: `1px solid ${t.ink}33`,
                  outlineOffset: "-1px",
                  background: `${t.ink}0C`,
                  overflow: "hidden",
                }}
              >
                {photo ? (
                  <div
                    aria-label={tt("variant.m01.photo_aria").replace("{names}", t.names)}
                    style={{
                      position: "absolute",
                      inset: 0,
                      backgroundImage: `url("${photo}")`,
                      backgroundSize: "cover",
                      backgroundPosition: "center",
                      filter: "sepia(0.18) saturate(0.85) contrast(0.96)",
                    }}
                  />
                ) : (
                  // Inline candle — neutral, sober placeholder until the user
                  // uploads their own portrait. Stroke uses the same ink as
                  // the rest of the layout so it disappears into the frame.
                  <svg
                    aria-hidden="true"
                    viewBox="0 0 200 280"
                    style={{ position: "absolute", inset: 0, width: "100%", height: "100%" }}
                  >
                    <ellipse cx="100" cy="92" rx="7" ry="14" fill="#C8804B" opacity="0.5" />
                    <ellipse cx="100" cy="94" rx="3" ry="8" fill="#F4D38A" />
                    <line x1="100" y1="102" x2="100" y2="120" stroke={t.ink} strokeWidth="1.5" opacity="0.7" />
                    <ellipse cx="100" cy="120" rx="16" ry="4" fill="none" stroke={t.ink} strokeWidth="1.4" opacity="0.7" />
                    <line x1="84" y1="120" x2="84" y2="228" stroke={t.ink} strokeWidth="1.6" opacity="0.7" />
                    <line x1="116" y1="120" x2="116" y2="228" stroke={t.ink} strokeWidth="1.6" opacity="0.7" />
                    <ellipse cx="100" cy="228" rx="28" ry="6" fill="none" stroke={t.ink} strokeWidth="1.4" opacity="0.7" />
                    <line x1="72" y1="228" x2="128" y2="228" stroke={t.ink} strokeWidth="1.6" opacity="0.7" />
                  </svg>
                )}
              </div>
            </div>

            <h1
              className="tinch-name"
              style={{
                fontFamily: t.font,
                fontSize: "clamp(40px, 6vw, 64px)",
                fontWeight: 400,
                margin: 0,
                letterSpacing: "0.02em",
                lineHeight: 1.1,
              }}
            >
              {t.names}
            </h1>

            {/* Years — the typographic moment. Italic, em-dash, hairline rule. */}
            <div style={{ marginTop: 20, marginBottom: 48 }}>
              <div
                className="tinch-years-line"
                style={{
                  fontFamily: t.font,
                  fontStyle: "italic",
                  fontSize: 24,
                  color: `${t.ink}cc`,
                  letterSpacing: "0.18em",
                  fontVariantNumeric: "lining-nums",
                }}
              >
                1948{" — "}2026
              </div>
              <div
                aria-hidden
                className="tinch-years-rule"
                style={{
                  height: 1,
                  width: 64,
                  background: `${t.ink}55`,
                  margin: "16px auto 0",
                }}
              />
            </div>

            <p
              className="tinch-hero-copy"
              style={{
                fontFamily: "var(--font-body)",
                fontStyle: "italic",
                fontSize: 19,
                lineHeight: 1.85,
                color: `${t.ink}cc`,
                marginBottom: 48,
              }}
            >
              {tt("variant.m01.hero.body")}
            </p>

            <div
              className="tinch-marosim"
              style={{
                padding: "24px 36px",
                border: `1px solid ${t.ink}44`,
                display: "inline-block",
              }}
            >
              <div
                style={{
                  fontFamily: "var(--font-ui)",
                  fontSize: 10,
                  fontWeight: 700,
                  letterSpacing: "0.36em",
                  color: `${t.ink}aa`,
                  marginBottom: 10,
                }}
              >
                {tt("variant.m01.marosim.label")}
              </div>
              <div
                className="tinch-marosim-date"
                style={{ fontFamily: t.font, fontSize: 22, fontStyle: "italic" }}
              >
                {t.date} · {t.venue}
              </div>
            </div>
          </QuietReveal>

          {/* Signature: a single candle in the hero corner. */}
          <div
            aria-hidden
            className="tinch-candle-wrap"
            style={{
              position: "absolute",
              right: "max(24px, 5vw)",
              bottom: "max(24px, 5vw)",
              opacity: 0.7,
            }}
          >
            <Candle ink={t.ink} accent={t.accent} bg={t.bg} reduce={reduce} size={36} />
          </div>
        </section>

        {/* ── QURANIC RECITATION (centerpiece) ──────────────────────── */}
        <section
          className="tinch-recitation"
          style={{
            padding: "120px 32px",
            borderTop: `1px solid ${t.ink}22`,
            textAlign: "center",
          }}
        >
          <QuietReveal style={{ maxWidth: 720, margin: "0 auto" }}>
            {/* Arabic — set in the same display serif (Marcellus); the system
                font fallback chain handles non-Latin glyphs gracefully. */}
            <p
              lang="ar"
              dir="rtl"
              className="tinch-arabic"
              style={{
                fontFamily: t.font,
                fontSize: "clamp(28px, 4vw, 44px)",
                lineHeight: 1.7,
                color: t.ink,
                margin: 0,
                letterSpacing: "0.02em",
              }}
            >
              إِنَّا لِلَّٰهِ وَإِنَّا إِلَيْهِ رَاجِعُونَ
            </p>

            <div
              aria-hidden
              style={{
                height: 1,
                width: 48,
                background: `${t.ink}33`,
                margin: "32px auto",
              }}
            />

            <p
              className="tinch-translit"
              style={{
                fontFamily: t.font,
                fontSize: "clamp(22px, 2.6vw, 30px)",
                fontStyle: "italic",
                lineHeight: 1.7,
                color: `${t.ink}dd`,
                margin: 0,
                letterSpacing: "0.01em",
              }}
            >
              {tt("variant.m01.recitation.translit")}
            </p>

            <p
              className="tinch-meaning"
              style={{
                fontFamily: "var(--font-body)",
                fontStyle: "italic",
                fontSize: 16,
                lineHeight: 1.7,
                color: `${t.ink}99`,
                marginTop: 24,
                maxWidth: 540,
                marginLeft: "auto",
                marginRight: "auto",
              }}
            >
              {tt("variant.m01.recitation.meaning")}
            </p>
          </QuietReveal>
        </section>

        {/* ── DU'A / YORDAM (in-lieu-of-flowers, Uzbek/Islamic) ─────── */}
        <section
          className="tinch-dua"
          style={{
            padding: "100px 32px",
            borderTop: `1px solid ${t.ink}22`,
            textAlign: "center",
          }}
        >
          <QuietReveal style={{ maxWidth: 600, margin: "0 auto" }} className="tinch-dua-inner">
            <div
              style={{
                fontFamily: "var(--font-ui)",
                fontSize: 11,
                fontWeight: 700,
                letterSpacing: "0.4em",
                color: `${t.ink}aa`,
                marginBottom: 28,
              }}
            >
              {tt("variant.m01.dua.label")}
            </div>
            <p
              className="tinch-dua-line"
              style={{
                fontFamily: t.font,
                fontStyle: "italic",
                fontSize: "clamp(22px, 2.4vw, 28px)",
                lineHeight: 1.65,
                color: t.ink,
                margin: 0,
              }}
            >
              {tt("variant.m01.dua.line")}
            </p>
            <p
              className="tinch-dua-sub"
              style={{
                fontFamily: "var(--font-body)",
                fontStyle: "italic",
                fontSize: 15,
                lineHeight: 1.8,
                color: `${t.ink}99`,
                marginTop: 24,
              }}
            >
              {tt("variant.m01.dua.sub")}
            </p>
          </QuietReveal>
        </section>

        {/* ── XOTIRA DAFTARI (single-line, inline, local) ───────────── */}
        <section
          className="tinch-ledger"
          style={{
            padding: "100px 32px",
            borderTop: `1px solid ${t.ink}22`,
            textAlign: "center",
          }}
        >
          <QuietReveal style={{ maxWidth: 560, margin: "0 auto" }}>
            <div
              style={{
                fontFamily: "var(--font-ui)",
                fontSize: 11,
                fontWeight: 700,
                letterSpacing: "0.4em",
                color: `${t.ink}aa`,
                marginBottom: 16,
              }}
            >
              {tt("variant.m01.ledger.label")}
            </div>
            <h2
              style={{
                fontFamily: t.font,
                fontStyle: "italic",
                fontSize: "clamp(28px, 3vw, 36px)",
                fontWeight: 400,
                margin: 0,
                color: t.ink,
              }}
            >
              {tt("variant.m01.ledger.title")}
            </h2>

            {thanked ? (
              <p
                className="tinch-ledger-thanks"
                style={{
                  fontFamily: t.font,
                  fontStyle: "italic",
                  fontSize: 22,
                  color: `${t.ink}cc`,
                  marginTop: 40,
                  letterSpacing: "0.02em",
                }}
              >
                {tt("variant.m01.ledger.thanks")}
              </p>
            ) : (
              <div style={{ marginTop: 32, textAlign: "left" }}>
                <input
                  value={memoryName}
                  onChange={(e) => setMemoryName(e.target.value)}
                  placeholder={tt("variant.m01.ledger.name_placeholder")}
                  aria-label={tt("variant.m01.ledger.name_placeholder")}
                  className="tinch-ledger-input"
                  style={{
                    width: "100%",
                    padding: "12px 0",
                    border: "none",
                    borderBottom: `1px solid ${t.ink}44`,
                    background: "transparent",
                    color: t.ink,
                    fontFamily: "var(--font-body)",
                    fontSize: 16,
                    outline: "none",
                    marginBottom: 20,
                  }}
                />
                <input
                  value={memoryLine}
                  onChange={(e) => setMemoryLine(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") onLeaveMemory();
                  }}
                  placeholder={tt("variant.m01.ledger.line_placeholder")}
                  aria-label={tt("variant.m01.ledger.line_placeholder")}
                  className="tinch-ledger-input"
                  style={{
                    width: "100%",
                    padding: "12px 0",
                    border: "none",
                    borderBottom: `1px solid ${t.ink}44`,
                    background: "transparent",
                    color: t.ink,
                    fontFamily: "var(--font-body)",
                    fontStyle: "italic",
                    fontSize: 16,
                    outline: "none",
                  }}
                />
                <div style={{ marginTop: 24, textAlign: "center" }}>
                  <button
                    onClick={onLeaveMemory}
                    disabled={!memoryName.trim() || !memoryLine.trim()}
                    className="tinch-ledger-submit"
                    style={{
                      padding: "10px 24px",
                      background: "transparent",
                      color: t.ink,
                      border: `1px solid ${t.ink}55`,
                      fontFamily: "var(--font-ui)",
                      fontSize: 11,
                      fontWeight: 700,
                      letterSpacing: "0.3em",
                      cursor:
                        !memoryName.trim() || !memoryLine.trim() ? "not-allowed" : "pointer",
                      opacity: !memoryName.trim() || !memoryLine.trim() ? 0.4 : 1,
                      transition: "opacity .6s ease",
                    }}
                  >
                    {tt("variant.m01.ledger.submit")}
                  </button>
                </div>
              </div>
            )}
          </QuietReveal>
        </section>

        <QuietReveal>
          <MapSection
            t={t}
            isDark={isDark}
            addressLine={`${t.venue} · ${tt("variant.m01.map.address")}`}
          />
        </QuietReveal>

        <QuietReveal>
          <ShareSection t={t} />
        </QuietReveal>

        <GuestFooter t={t} />
      </div>
    </>
  );
}
